import { ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type ProfileCheckState =
  | { status: "checking" }
  | { status: "complete" }
  | {
      status: "incomplete";
      profile: {
        first_name: string | null;
        last_name: string | null;
        company_position: string | null;
        personal_phone_number: string | null;
        profile_photo_url: string | null;
      };
    }
  | { status: "error"; message: string };

const initialsOf = (first: string, last: string, email: string): string => {
  const a = (first || "").trim().charAt(0);
  const b = (last || "").trim().charAt(0);
  const fallback = (email || "?").trim().charAt(0);
  return (a + b || fallback).toUpperCase();
};

const ProfileCompletionGate = ({
  userId,
  email,
  initial,
  onComplete,
}: {
  userId: string;
  email: string;
  initial: {
    first_name: string | null;
    last_name: string | null;
    company_position: string | null;
    personal_phone_number: string | null;
    profile_photo_url: string | null;
  };
  onComplete: () => void;
}) => {
  const [firstName, setFirstName] = useState(initial.first_name ?? "");
  const [lastName, setLastName] = useState(initial.last_name ?? "");
  const [phone, setPhone] = useState(initial.personal_phone_number ?? "");
  const [role, setRole] = useState(initial.company_position ?? "");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(initial.profile_photo_url);
  const [photoUrl, setPhotoUrl] = useState<string | null>(initial.profile_photo_url);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);

    const previewObjectUrl = URL.createObjectURL(file);
    setPhotoPreview(previewObjectUrl);

    const ext = file.type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'jpg';
    const path = `${userId}/avatar.${ext}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(path);

      setPhotoUrl(data.publicUrl);
    } catch (err) {
      console.error('Photo upload error:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !role.trim()) {
      setError("All fields are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { error: profileErr } = await supabase
        .from("profiles")
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          company_position: role.trim(),
          personal_phone_number: phone.trim(),
          profile_photo_url: photoUrl,
          profile_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);
      if (profileErr) throw profileErr;

      toast.success("Profile completed");
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save profile.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="bg-slate-900 border-slate-800 text-slate-100 w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-white">Complete Your Profile</CardTitle>
          <CardDescription className="text-slate-400">
            Please complete your profile before accessing the admin portal.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Profile preview"
                  className="h-16 w-16 rounded-full object-cover border border-slate-700"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 text-lg font-semibold">
                  {initialsOf(firstName, lastName, email)}
                </div>
              )}
              <div>
                <Label
                  htmlFor="gate-photo"
                  className="text-amber-400 hover:text-amber-300 text-sm cursor-pointer"
                >
                  {photoFile || photoPreview ? "Change photo" : "Upload photo"}
                </Label>
                <input
                  id="gate-photo"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="gate-first" className="text-slate-300">First name</Label>
                <Input
                  id="gate-first"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gate-last" className="text-slate-300">Last name</Label>
                <Input
                  id="gate-last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="gate-phone" className="text-slate-300">Phone number</Label>
              <Input
                id="gate-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="bg-slate-950 border-slate-700 text-slate-100"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="gate-role" className="text-slate-300">Your role / position</Label>
              <Input
                id="gate-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g. Platform Manager, CTO"
                required
                className="bg-slate-950 border-slate-700 text-slate-100"
              />
            </div>

            {error && <p className="text-sm text-red-400">{error}</p>}
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950"
            >
              {submitting ? "Saving…" : "Complete Setup"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </main>
  );
};

export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { session, isSuperuser, isLoading, signOut } = useAuth();
  const [profileState, setProfileState] = useState<ProfileCheckState>({ status: "checking" });

  const userId = session?.user?.id ?? null;

  useEffect(() => {
    let cancelled = false;
    if (!userId || !isSuperuser) {
      setProfileState({ status: "checking" });
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select(
          "profile_completed, first_name, last_name, company_position, personal_phone_number, profile_photo_url",
        )
        .eq("id", userId)
        .single();
      if (cancelled) return;
      if (error) {
        setProfileState({ status: "error", message: error.message });
        return;
      }
      const complete =
        data?.profile_completed === true &&
        (data?.first_name ?? "").trim().length > 0 &&
        (data?.last_name ?? "").trim().length > 0;
      if (complete) {
        setProfileState({ status: "complete" });
      } else {
        setProfileState({
          status: "incomplete",
          profile: {
            first_name: data?.first_name ?? null,
            last_name: data?.last_name ?? null,
            company_position: data?.company_position ?? null,
            personal_phone_number: data?.personal_phone_number ?? null,
            profile_photo_url: data?.profile_photo_url ?? null,
          },
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, isSuperuser]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div
          className="h-12 w-12 rounded-full border-4 border-muted border-t-primary animate-spin"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperuser) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-semibold text-foreground">Access denied</h1>
          <p className="text-muted-foreground">
            This portal is restricted to platform administrators.
          </p>
          <Button onClick={signOut} variant="outline">
            Sign Out
          </Button>
        </div>
      </main>
    );
  }

  if (profileState.status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div
          className="h-12 w-12 rounded-full border-4 border-muted border-t-primary animate-spin"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (profileState.status === "error") {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-semibold text-foreground">Unable to load profile</h1>
          <p className="text-muted-foreground">{profileState.message}</p>
          <Button onClick={signOut} variant="outline">Sign Out</Button>
        </div>
      </main>
    );
  }

  if (profileState.status === "incomplete" && userId) {
    return (
      <ProfileCompletionGate
        userId={userId}
        email={session.user.email ?? ""}
        initial={profileState.profile}
        onComplete={() => setProfileState({ status: "complete" })}
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
